import { useMemo, useState } from "react";
import { IconCheck, IconPlus, IconX } from "@tabler/icons-react";
import { useApi } from "../../../../swr/useApi";
import { adminProductsService } from "../../../api_client";
import { SearchBar } from "../../../../components/common/input";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { Image } from "../../../../components/common/image";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer/shimmer";
import type { ProductDto, ProductsQueryDto, ProductsResponseDto } from "../../../../api";

interface PromotionProductPickerProps {
  /** Selected product templates: id → display name. */
  selected: Map<number, string>;
  onChange: (next: Map<number, string>) => void;
}

const PAGE_SIZE = 6;

/**
 * Search the catalog (with thumbnails) and add/remove product templates for a
 * PRODUCTS-scope promotion. Mirrors the featured-deals admin browse experience.
 */
export function PromotionProductPicker({ selected, onChange }: PromotionProductPickerProps) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const query: ProductsQueryDto = { page, pageSize: PAGE_SIZE, search: search.trim() || undefined };
  const key = `promo-product-search-${JSON.stringify(query)}`;
  const { data, isLoading } = useApi<ProductsResponseDto>(key, () =>
    adminProductsService.adminGetProducts(query),
  );
  const products = useMemo(() => data?.data ?? [], [data]);
  const pageCount = data?.meta?.pageCount ?? 1;

  const toggle = (id: number, name: string) => {
    const next = new Map(selected);
    if (next.has(id)) next.delete(id);
    else next.set(id, name);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-3">
      {selected.size > 0 && (
        <div className="flex flex-wrap gap-2">
          {[...selected.entries()].map(([id, name]) => (
            <Chip variant="primary" key={id}>
              <span className="flex items-center gap-1">
                {name}
                <button type="button" onClick={() => toggle(id, name)} aria-label={`Remove ${name}`}>
                  <IconX size={14} />
                </button>
              </span>
            </Chip>
          ))}
        </div>
      )}

      <SearchBar
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        placeholder="Search products to add"
      />

      {isLoading ? (
        <ShimmerLoader message="Searching products…">
          <Shimmer className="h-20 rounded-2xl" />
          <Shimmer className="h-20 rounded-2xl" />
        </ShimmerLoader>
      ) : products.length === 0 ? (
        <Text variant="bodySm" tone="muted">No products match.</Text>
      ) : (
        <div className="flex flex-col gap-2">
          {products.map((p: ProductDto) => {
            const name = p.displayName ?? p.name ?? `Product #${p.id}`;
            const isSelected = selected.has(p.id);
            return (
              <article
                key={p.id}
                className="flex items-center gap-4 rounded-2xl border border-borderDefault bg-bgWhite p-3 transition-colors hover:border-borderDark"
              >
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-bgLight">
                  <Image image={p.image} alt={name} size="w-full h-full" objectFit="contain" showPlaceholder />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <Text variant="bodyBold" tone="dark" className="line-clamp-1">{name}</Text>
                  <div className="flex flex-wrap items-center gap-x-3">
                    {p.brand?.name && <Text variant="bodyXs" tone="muted">{p.brand.name}</Text>}
                    {p.category?.name && <Text variant="bodyXs" tone="muted">{p.category.name}</Text>}
                    {p.defaultCode && <Text variant="bodyXs" tone="muted">SKU {p.defaultCode}</Text>}
                  </div>
                </div>
                <Button
                  type="button"
                  variant={isSelected ? "secondary" : "primary"}
                  size="sm"
                  leftIcon={isSelected ? <IconCheck size={16} /> : <IconPlus size={16} />}
                  onClick={() => toggle(p.id, name)}
                >
                  {isSelected ? "Added" : "Add"}
                </Button>
              </article>
            );
          })}
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-between">
          <Button type="button" variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Text variant="bodyXs" tone="muted">
            Page {data?.meta?.currentPage ?? page} of {pageCount}
          </Text>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
